import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';

/**
 * Zoom mapy po wycentrowaniu na użytkowniku (pierwsze kliknięcie „Zlokalizuj mnie”).
 * Wyższa wartość = bliżej. Zmiana tylko tutaj wpływa na przybliżenie po lokalizacji.
 */
export const USER_LOCATION_FOCUS_ZOOM = 14;

/**
 * Lokalizacja użytkownika na mapie Leaflet
 */
export function useUserLocation(mapRef, isLoaded) {
  const { t } = useTranslation(['discover']);
  const [userLocation, setUserLocation] = useState(null);
  const userLocationRef = useRef(null);
  userLocationRef.current = userLocation;
  const userMarkerRef = useRef(null);
  const pulseCircleRef = useRef(null);
  const pulseIntervalRef = useRef(null);

  const focusUserOnMap = useCallback(() => {
    const map = mapRef.current;
    const loc = userLocationRef.current;
    if (!map || loc == null || loc.lat == null || loc.lng == null) {
      return;
    }
    map.setView([loc.lat, loc.lng], USER_LOCATION_FOCUS_ZOOM, { animate: true });
  }, [mapRef]);

  const getUserLocation = useCallback(() => {
    if (!navigator.geolocation) {
      console.warn('Geolocation is not supported by this browser.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      },
      (error) => {
        console.warn('Error getting user location:', error);
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      }
    );
  }, []);

  /** Po uzyskaniu lokalizacji wyśrodkuj mapę (także gdy GPS wróci przed mapReady). */
  useEffect(() => {
    if (!isLoaded || !userLocation) {
      return;
    }
    focusUserOnMap();
  }, [isLoaded, userLocation, focusUserOnMap]);

  const createUserLocationMarker = useCallback(() => {
    const map = mapRef.current;
    if (!map || !userLocation) {
      return;
    }

    if (userMarkerRef.current) {
      if (pulseIntervalRef.current) {
        clearInterval(pulseIntervalRef.current);
        pulseIntervalRef.current = null;
      }
      if (pulseCircleRef.current) {
        map.removeLayer(pulseCircleRef.current);
        pulseCircleRef.current = null;
      }
      map.removeLayer(userMarkerRef.current);
      userMarkerRef.current = null;
    }

    const ll = L.latLng(userLocation.lat, userLocation.lng);

    const icon = L.divIcon({
      className: 'hb-user-location-marker',
      html: '<div class="hb-user-location-dot"></div>',
      iconSize: [16, 16],
      iconAnchor: [8, 8],
    });

    const marker = L.marker(ll, { icon, zIndexOffset: 2000, title: t('discover.userLocation.current', 'Twoja lokalizacja') });
    marker.addTo(map);
    userMarkerRef.current = marker;

    const circle = L.circle(ll, {
      radius: 50,
      color: '#fd7651',
      weight: 2,
      opacity: 0.45,
      fillColor: '#fd7651',
      fillOpacity: 0.18,
      interactive: false,
    }).addTo(map);
    pulseCircleRef.current = circle;

    let radius = 50;
    let growing = true;
    pulseIntervalRef.current = setInterval(() => {
      if (growing) {
        radius += 2;
        if (radius >= 100) growing = false;
      } else {
        radius -= 2;
        if (radius <= 50) growing = true;
      }
      circle.setRadius(radius);
    }, 50);
  }, [userLocation, mapRef, t]);

  const centerOnUserLocation = focusUserOnMap;

  useEffect(() => {
    const map = mapRef.current;
    if (map && isLoaded && userLocation) {
      createUserLocationMarker();
    }

    return () => {
      const mapCleanup = map;
      if (pulseIntervalRef.current) {
        clearInterval(pulseIntervalRef.current);
        pulseIntervalRef.current = null;
      }
      if (mapCleanup && pulseCircleRef.current) {
        mapCleanup.removeLayer(pulseCircleRef.current);
        pulseCircleRef.current = null;
      }
      if (mapCleanup && userMarkerRef.current) {
        mapCleanup.removeLayer(userMarkerRef.current);
        userMarkerRef.current = null;
      }
    };
  }, [userLocation, isLoaded, createUserLocationMarker, mapRef]);

  return {
    userLocation,
    getUserLocation,
    centerOnUserLocation,
    createUserLocationMarker,
  };
}

export default useUserLocation;
